import { faUserHeadset } from "@fortawesome/pro-solid-svg-icons";

import {
	Anchor,
	Button,
	CloseDialogSlotWithScroll,
	VideoPlayer,
	VideoPlayerDialog,
	cn,
	getLocaleConstants,
	separateLocale,
	useServiceContext,
} from "@tosize/ui";
import { Fragment, useMemo } from "react";
import { useIntl } from "react-intl";
import { match } from "ts-pattern";
import { useConfig } from "../../../context/ConfigContext";
import { AppModus, useCanvasStore } from "../../../hooks/useCanvasStore";
import FloatingButton from "../../FloatingButton";
import AllCompartmentsContent from "./Content/AllCompartmentsContent";
import FormContent from "./Content/FormContent";
import SingleCompartmentContent from "./Content/SingleCompartmentContent";
import FloatingMenuButton from "./FloatingMenuButton";
import MenuTitleBlock from "./TitleBlock";

export default function Menu() {
	const intl = useIntl();
	const { furniture } = getLocaleConstants(intl.locale);
	const { routes, mode } = useConfig();
	const { appMode } = useCanvasStore();
	const { onOpenHandler, chatHandler, chatAvailable, chatActive } = useServiceContext();

	const titleBlockProps = useMemo(() => {
		switch (appMode) {
			case AppModus.form:
				return {
					surtitle: intl.formatMessage({
						defaultMessage: "Start met de basis",
						id: "to4922",
					}),
					title: intl.formatMessage({
						defaultMessage: "Configureer je kast in 3 eenvoudige stappen",
						id: "AJNtuK",
					}),
				};
			case AppModus.layout:
				return {
					surtitle: intl.formatMessage({
						defaultMessage: "Jij ontwerpt jouw ideale meubel",
						id: "Rdts/X",
					}),
					title: intl.formatMessage({
						defaultMessage: "Pas de indeling aan",
						id: "iYaToY",
					}),
				};
			case AppModus.compartment:
				return {
					surtitle: intl.formatMessage({
						defaultMessage: "Tot in detail naar wens",
						id: "HkEnP3",
					}),
					title: intl.formatMessage({
						defaultMessage: "Wijzig vakopties",
						id: "fVHbvE",
					}),
				};
		}
	}, [appMode, intl]);

	const videoId = match(separateLocale(intl.locale).language)
		.with("nl", () => "1003544963")
		.with("fr", () => "1038185735")
		.with("de", () => "1038893835")
		.otherwise(() => undefined);

	return (
		<div
			className={cn(
				"container flex flex-col items-start p-4 xl:absolute xl:top-0 xl:left-0 xl:mt-4 xl:ml-4 xl:max-h-[calc(100%-2rem)] xl:w-[29rem] xl:rounded-xl",
				{
					"xl:bg-transparent": !appMode,
					"xl:bg-shades-light xl:pr-8 xl:shadow xl:backdrop-blur-3xl": !!appMode,
				},
			)}
		>
			<div className="z-3 child:mr-4 flex items-start last:child:mr-0 xl:absolute xl:flex-col xl:justify-center xl:space-x-0 xl:space-y-4">
				<FloatingMenuButton
					step="1"
					label={intl.formatMessage({
						defaultMessage: "Afmetingen en materialen",
						id: "9ghEyB",
					})}
					mode={AppModus.form}
				/>

				<FloatingMenuButton
					step="2"
					label={intl.formatMessage({
						defaultMessage: "Indeling aanpassen",
						id: "NdVfW3",
					})}
					mode={AppModus.layout}
				/>

				<FloatingMenuButton
					step="3"
					label={intl.formatMessage({
						defaultMessage: "Vakopties wijzigen",
						id: "BP/PXd",
					})}
					mode={AppModus.compartment}
				/>

				{chatAvailable && mode === "add" && (
					<FloatingButton
						className="ml-auto xl:hidden"
						onClick={async () => {
							onOpenHandler();
							await chatHandler();
						}}
						active={chatActive}
						icon={faUserHeadset}
						label={intl.formatMessage({ defaultMessage: "Hulp nodig?", id: "HmZHhI" })}
						side="right"
					/>
				)}
			</div>

			<div
				className={cn({
					"xl:scrollbar-none mt-4 ml-0 w-full xl:mt-0 xl:ml-[calc(40px+2rem)] xl:w-auto xl:min-w-[calc(100%-(40px+2rem))] xl:overflow-y-auto":
						!!appMode,
				})}
			>
				{!!appMode && (
					<Fragment>
						<MenuTitleBlock {...titleBlockProps} />

						{match(appMode)
							.with(AppModus.form, () => <FormContent />)
							.with(AppModus.layout, () => <AllCompartmentsContent />)
							.with(AppModus.compartment, () => <SingleCompartmentContent />)
							.run()}

						{!!videoId && (
							<VideoPlayer
								variant="vimeo"
								id={videoId}
								wrapperClassName="mx-auto mt-4 max-w-lg"
								title={intl.formatMessage({
									defaultMessage: "Uitleg over de TOSIZE Furniture configurator",
									id: "Ow6JOU",
								})}
							/>
						)}

						{!!furniture.assemblyVideoId && (
							<VideoPlayerDialog
								player={{
									variant: "vimeo",
									id: furniture.assemblyVideoId,
									title: intl.formatMessage({
										defaultMessage: "Bekijk montage instructie",
										id: "cXTGIn",
									}),
								}}
								title={intl.formatMessage({
									defaultMessage: "Bekijk montage instructie",
									id: "cXTGIn",
								})}
								content={
									<div className="mt-4 flex justify-between gap-4">
										<CloseDialogSlotWithScroll id="faq-items">
											<Button variant="link">
												{intl.formatMessage({
													defaultMessage: "Bekijk veelgestelde vragen",
													id: "5H1zNC",
												})}
											</Button>
										</CloseDialogSlotWithScroll>

										{routes?.get("customer_service/video_tutorials/tosize-furniture") && (
											<Anchor
												target="_blank"
												href={routes?.get("customer_service/video_tutorials/tosize-furniture")}
											>
												{intl.formatMessage({
													defaultMessage: "Bekijk alle video's",
													id: "Ul4+W4",
												})}
											</Anchor>
										)}
									</div>
								}
							>
								<Button variant="link" className="mx-auto mt-4 block max-w-fit text-center xl:mt-2">
									{intl.formatMessage({
										defaultMessage: "Bekijk montage instructie",
										id: "cXTGIn",
									})}
								</Button>
							</VideoPlayerDialog>
						)}
					</Fragment>
				)}
			</div>
		</div>
	);
}
